import { state, api, $, $$, toast, escHtml, truncate, _updateHelpBadge, _ensureMachineCache, openAlarmHistoryForMachine } from './core';
import { _initLabelScan, _initPhotoUpload } from './config';
import { renderDetailTab, setDetailTabActive } from './line-detail';
import { loadJournalLog, urgShowPhase } from './journal';
import { loadReports } from './reports';
import { openRevisionDetailView } from './workflow';
import { goToOverviewDetail } from './tasks-calendar';
import { _applyRoleMenus } from './overview';
import { applyInitialAppRoute } from './app-route';
import { askConfirm } from './dialogs';
import { comutaTema } from './tema';

declare function showSection(secId: string): void;

/* ═══ AUTH / ROLE HANDLING ══════════════════════════════════════ */
export async function loadCurrentUser(): Promise<void> {
    try {
        const u = await api('/api/auth/me');
        state.currentUser = u || { username: null, role: 'operator' };
    } catch (_e: unknown) {
        state.currentUser = { username: null, role: 'operator' };
    }

    if (!state.currentUser.username) {
        return;
    }

    _hideAuthGate();
    const role = state.currentUser.role || 'operator';
    const roleLabels: Record<string, string> = { operator: 'Operator', engineer: 'Inginer', master: 'Master' };
    const lbl = document.getElementById('current-role-label');
    if (lbl) lbl.textContent = `Nivel: ${roleLabels[role] || role}`;
    _applyRoleMenus(role);
    _updateHelpBadge();
    _checkAiLocalStatus();
}

function _showMandatoryLogin(): void {
    const gate = document.getElementById('auth-gate');
    if (gate) gate.classList.remove('hidden');
    const appWrap = document.getElementById('app-wrapper');
    if (appWrap) appWrap.classList.add('auth-locked');

    fetch('/api/auth/usernames', { credentials: 'include' })
        .then(r => r.ok ? r.json() : [])
        .then(usernames => {
            const dl = document.getElementById('gate-usernames-list');
            if (dl && Array.isArray(usernames)) {
                dl.innerHTML = usernames.map((u: string) => `<option value="${escHtml(u)}">`).join('');
            }
        })
        .catch(() => { /* no suggestions */ });

    setTimeout(() => (document.getElementById('gate-username') as HTMLInputElement)?.focus(), 200);
}

let _gatePending2fa = false;
let _gatePending2faUser = '';
let _gatePending2faPwd = '';

(window as any)._gateLogin = _gateLogin;

async function _gateLogin(): Promise<void> {
    const userEl = document.getElementById('gate-username') as HTMLInputElement | null;
    const pwdEl = document.getElementById('gate-password') as HTMLInputElement | null;
    const totpEl = document.getElementById('gate-totp') as HTMLInputElement | null;
    const errEl = document.getElementById('gate-error');

    if (errEl) { errEl.style.display = 'none'; errEl.textContent = ''; }

    let user: string, pwd: string, totpCode = '';

    if (_gatePending2fa && totpEl) {
        user = _gatePending2faUser;
        pwd = _gatePending2faPwd;
        totpCode = totpEl.value.trim();
        if (!totpCode) {
            if (errEl) { errEl.textContent = 'Introdu codul 2FA.'; errEl.style.display = 'block'; }
            return;
        }
    } else {
        user = userEl?.value.trim() || '';
        pwd = pwdEl?.value || user;
        if (!user) {
            if (errEl) { errEl.textContent = 'Introdu numele de utilizator.'; errEl.style.display = 'block'; }
            return;
        }
    }

    const body: any = { username: user, password: pwd };
    if (totpCode) body.totp_code = totpCode;

    try {
        const res = await fetch('/api/auth/login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        const data = await res.json();

        if (!res.ok) {
            if (errEl) { errEl.textContent = data.error || 'Eroare autentificare'; errEl.style.display = 'block'; }
            return;
        }

        if (data.requires_2fa) {
            _gatePending2fa = true;
            _gatePending2faUser = user;
            _gatePending2faPwd = pwd;
            const totpRow = document.getElementById('gate-totp-row');
            if (totpRow) totpRow.style.display = '';
            if (errEl) { errEl.textContent = 'Introdu codul din aplicația authenticator.'; errEl.style.display = 'block'; errEl.style.color = 'var(--cyan)'; }
            (document.getElementById('gate-totp') as HTMLInputElement)?.focus();
            return;
        }

        _gatePending2fa = false;
        state.currentUser = data;
        const roleLabels: Record<string, string> = { operator: 'Operator', engineer: 'Inginer', master: 'Master' };
        const lbl = document.getElementById('current-role-label');
        if (lbl) lbl.textContent = `Nivel: ${roleLabels[String(data.role)] || data.role}`;
        _hideAuthGate();
        toast(`Autentificat ca ${roleLabels[String(data.role)] || data.role}`);
        _applyRoleMenus(String(data.role));
        _updateHelpBadge();
        _checkAiLocalStatus();
        _checkAiCloudStatus();
        _checkCollectorStatus();
        _fetchProactiveAlerts();
        try {
            const cfg = await (window as any).loadFactoryConfig?.();
            (window as any).applyFactoryTheme?.(cfg);
        } catch (_e) { /* ignore */ }
        applyInitialAppRoute();
        _checkClarificationNotifications(data);
        if (data.must_change_password) {
            setTimeout(() => toast('Trebuie să schimbați parola implicită!', 'error'), 500);
        }
    } catch (e) {
        if (errEl) { errEl.textContent = 'Eroare de conexiune.'; errEl.style.display = 'block'; }
    }
}

function _hideAuthGate(): void {
    const gate = document.getElementById('auth-gate');
    if (gate) gate.classList.add('hidden');
    const appWrap = document.getElementById('app-wrapper');
    if (appWrap) appWrap.classList.remove('auth-locked');
    const cancelBtn = document.querySelector('#modal-login [data-close="modal-login"]') as HTMLElement | null;
    if (cancelBtn) cancelBtn.style.display = '';
}

export let _aiMode: string = 'auto'; // 'auto' | 'local' | 'cloud'
export let _aiLocalOnline: boolean = false;

export function _syncAiModeButtons(mode: string): void {
    _aiMode = mode;
    document.querySelectorAll('.ai-mode-btn').forEach((b: Element) => {
        const hb = b as HTMLElement;
        hb.classList.toggle('active', hb.dataset.mode === mode);
    });
}

document.querySelectorAll('.ai-mode-selector').forEach((sel: Element) => {
    sel.addEventListener('click', (e: Event) => {
        const btn = (e.target as HTMLElement).closest('.ai-mode-btn') as HTMLElement | null;
        if (!btn) return;
        const m = btn.dataset.mode || 'auto';
        _syncAiModeButtons(m);
        const label = m === 'auto'
            ? 'Auto: începe la AI Local; dacă răspunsul e nesigur și Cloud e configurat, trece automat la Claude/OpenAI.'
            : m === 'local' ? ('Doar AI Local' + (_aiLocalOnline ? ' (online)' : ' (offline — nu va funcționa!)'))
            : 'Cloud: fiecare mesaj la Claude/OpenAI';
        toast(label, m === 'local' && !_aiLocalOnline ? 'error' : 'info');
    });
});

export async function _checkAiLocalStatus(): Promise<void> {
    try {
        const res = await fetch('/api/ai_local/status');
        const data = await res.json();
        _aiLocalOnline = data.available === true;
        const _modelLabel = data.model || 'Ollama';
        document.querySelectorAll('.ai-status-dot').forEach((dot: Element) => {
            dot.className = 'ai-status-dot ' + (_aiLocalOnline ? 'online' : 'offline');
            (dot as HTMLElement).title = _aiLocalOnline ? `AI Local activ — ${_modelLabel}` : 'AI Local offline';
        });
        document.querySelectorAll('.ai-mode-btn[data-mode="local"]').forEach((btn: Element) => {
            (btn as HTMLElement).title = _aiLocalOnline
                ? `Doar AI Local — model: ${_modelLabel} — ${data.rag_documents || 0} doc RAG`
                : 'AI Local offline — Ollama nu răspunde';
        });
        const indLocal = document.getElementById('ai-ind-local');
        if (indLocal) {
            const dot = indLocal.querySelector('.ai-ind-dot');
            if (dot) {
                if (_aiLocalOnline) {
                    dot.className = 'ai-ind-dot online';
                    indLocal.className = 'ai-indicator active';
                    indLocal.title = `AI Local activ — ${data.model || 'Ollama'} — ${data.rag_documents || 0} documente RAG`;
                } else {
                    dot.className = 'ai-ind-dot offline';
                    indLocal.className = 'ai-indicator down';
                    indLocal.title = 'AI Local offline — Ollama nu răspunde';
                }
            }
        }
    } catch (_e: unknown) {
        _aiLocalOnline = false;
        const indLocal = document.getElementById('ai-ind-local');
        if (indLocal) {
            indLocal.querySelector('.ai-ind-dot')!.className = 'ai-ind-dot offline';
            indLocal.className = 'ai-indicator down';
            indLocal.title = 'AI Local offline';
        }
    }
}

export async function _checkAiCloudStatus(): Promise<void> {
    if (!state.currentUser?.username) return;   // înainte de login dădea 401 (audit 4 sep 2026)
    try {
        const res = await fetch('/api/chat/status');
        const data = await res.json();
        const indCloud = document.getElementById('ai-ind-cloud');
        if (!indCloud) return;
        const dot = indCloud.querySelector('.ai-ind-dot')!;
        if (data.configured) {
            dot.className = 'ai-ind-dot online';
            indCloud.className = 'ai-indicator active';
            const prov = data.provider === 'claude' ? 'Claude' : 'OpenAI';
            indCloud.title = `AI Cloud activ — ${prov}`;
        } else {
            dot.className = 'ai-ind-dot unconfigured';
            indCloud.className = 'ai-indicator';
            indCloud.title = 'AI Cloud — cheie API neconfigurată (Setări API)';
        }
    } catch(_e: unknown) {
        const indCloud = document.getElementById('ai-ind-cloud');
        if (indCloud) {
            indCloud.querySelector('.ai-ind-dot')!.className = 'ai-ind-dot unconfigured';
            indCloud.className = 'ai-indicator';
            indCloud.title = 'AI Cloud — nu se poate verifica';
        }
    }
}

setInterval(_checkAiLocalStatus, 30000);
setInterval(_checkAiCloudStatus, 60000);
_checkAiCloudStatus();

/** Local time-ago (shadows core helper; kept for collector title formatting). */
function _timeAgo(isoStr: string): string {
    try {
        const d = new Date(isoStr.endsWith('Z') ? isoStr : isoStr + 'Z');
        const sec = Math.floor((Date.now() - d.getTime()) / 1000);
        if (sec < 60) return 'acum ' + sec + 's';
        if (sec < 3600) return 'acum ' + Math.floor(sec / 60) + ' min';
        if (sec < 86400) return 'acum ' + Math.floor(sec / 3600) + ' ore';
        return 'acum ' + Math.floor(sec / 86400) + ' zile';
    } catch (_e: unknown) {
        return isoStr;
    }
}

export async function _checkCollectorStatus(): Promise<void> {
    if (!state.currentUser?.username) return;   // înainte de login dădea 401 (audit 4 sep 2026)
    try {
        const res = await fetch('/api/collector/status', {credentials:'include'});
        const data = await res.json();
        const ind = document.getElementById('collector-ind');
        const dot = document.getElementById('collector-ind-dot');
        if (!ind || !dot) return;
        const rc = data.remote_collector || {};
        const tacute: string[] = Array.isArray(rc.colectoare_tacute) ? rc.colectoare_tacute : [];
        const versiuni = Array.isArray(rc.build) ? rc.build.join(', ') : (rc.build || '');
        if (rc.online && tacute.length) {
            // R5: unul sau mai multe colectoare tac, restul trimit — nu e „totul bine"
            dot.className = 'ai-ind-dot warning';
            ind.className = 'ai-indicator active';
            ind.title = `Colectoare: ${rc.colectoare_online} din ${rc.colectoare_total} trimit — TAC: `
                + tacute.map(t => t.replace(/^colector_/, '')).join(', ')
                + (versiuni ? ` — versiuni: ${versiuni}` : '');
        } else if (rc.online) {
            dot.className = 'ai-ind-dot online';
            ind.className = 'ai-indicator active';
            const ago = rc.last_heartbeat ? _timeAgo(rc.last_heartbeat) : '';
            const protos = Object.entries(rc.protocols||{}).filter(([,v])=>v).map(([k])=>k).join(', ');
            const cate = rc.colectoare_total ? `${rc.colectoare_online} colectoare, ` : '';
            ind.title = `Colector v2 ONLINE — ${cate}${rc.sources||0} surse, ${rc.ok||0} OK, ${rc.fail||0} erori${ago ? ' — ultimul semnal: '+ago : ''}${versiuni ? ' — versiuni: '+versiuni : ''}${protos ? ' — protocoale: '+protos : ''}`;
        } else if (rc.last_heartbeat) {
            dot.className = 'ai-ind-dot offline';
            ind.className = 'ai-indicator down';
            ind.title = `Colector v2 OFFLINE — ultimul semnal: ${_timeAgo(rc.last_heartbeat)} — posibil oprit`;
        } else {
            dot.className = 'ai-ind-dot unconfigured';
            ind.className = 'ai-indicator';
            ind.title = 'Colector v2 — niciun semnal primit. Verificați dacă rulează pe PC-ul din fabrică.';
        }
    } catch(_e: unknown) {
        const dot = document.getElementById('collector-ind-dot');
        if (dot) { dot.className = 'ai-ind-dot unconfigured'; }
    }
}

setInterval(_checkCollectorStatus, 15000);
_checkCollectorStatus();

export let _proactiveAlerts: any[] = [];
export async function _fetchProactiveAlerts(): Promise<void> {
    if (!state.currentUser?.username) return;   // înainte de login dădea 401 (audit 4 sep 2026)
    try {
        const res = await fetch('/api/ai/proactive_alerts');
        _proactiveAlerts = await res.json();
        const btn = document.getElementById('proactive-alerts-btn');
        const countEl = document.getElementById('pa-count');
        if (!btn || !countEl) return;
        if (_proactiveAlerts.length > 0) {
            btn.style.display = '';
            countEl.textContent = String(_proactiveAlerts.length);
            const highCount = _proactiveAlerts.filter((a: any) => a.severity === 'high').length;
            if (highCount > 0) {
                countEl.style.background = 'var(--red)';
                btn.style.borderColor = 'var(--red)';
            } else {
                countEl.style.background = 'var(--orange)';
                btn.style.borderColor = 'var(--orange)';
            }
        } else {
            btn.style.display = 'none';
        }
    } catch(_e: unknown) {}
}
_fetchProactiveAlerts();
setInterval(_fetchProactiveAlerts, 60000);

export function _closeProactivePanel(): void {
    document.getElementById('proactive-alerts-panel')?.classList.add('hidden');
}

document.getElementById('proactive-alerts-btn')?.addEventListener('click', (e: MouseEvent) => {
    e.stopPropagation();
    const panel = document.getElementById('proactive-alerts-panel');
    if (!panel) return;
    const isOpen = !panel.classList.contains('hidden');
    if (isOpen) {
        _closeProactivePanel();
        return;
    }
    panel.classList.remove('hidden');
    const list = document.getElementById('pa-list');
    if (!list) return;
    if (_proactiveAlerts.length === 0) {
        list.innerHTML = '<p style="padding:16px;text-align:center;color:var(--text-muted)">Nicio alertă activă</p>';
    } else {
        const typeLabels: Record<string, string> = {
            anomaly: 'Anomalie senzor',
            overdue: 'Interval depășit',
            status: 'Status mașină',
            controller_alarm: 'Alarmă controler',
        };
        list.innerHTML = _proactiveAlerts.map((a: any, idx: number) => `
            <div class="pa-item sev-${a.severity}" data-pa-idx="${idx}">
                <div class="pa-machine">${escHtml(a.machine_id)}</div>
                <div class="pa-msg">${escHtml(a.message)}</div>
                <div class="pa-type">${typeLabels[a.type] || a.type} — ${a.severity}</div>
            </div>`).join('');
        list.querySelectorAll('.pa-item').forEach((item: Element) => {
            item.addEventListener('click', async () => {
                const idx = parseInt((item as HTMLElement).dataset.paIdx || '-1', 10);
                const alert = _proactiveAlerts[idx];
                _closeProactivePanel();
                if (alert?.type === 'controller_alarm' && alert.alarm_code) {
                    const { openControllerAlarmDetailModal } = await import('./alarm-manual');
                    openControllerAlarmDetailModal({
                        machine_id: alert.machine_id,
                        machine_name: alert.machine_id,
                        alarm_code: String(alert.alarm_code),
                        alarm_type: alert.alarm_type,
                        severity: alert.severity === 'high' ? 'critical' : 'warning',
                        action: alert.message,
                        title: '🔔 Alarmă controler',
                        onHistory: () => { void openAlarmHistoryForMachine(alert.machine_id); },
                        onSensors: () => {
                            (window as any).detailTabActive = 'sensors';
                            if ((window as any).openLineDetail) (window as any).openLineDetail(alert.machine_id);
                        },
                        onMachine: () => {
                            (window as any).detailTabActive = 'parts';
                            if ((window as any).openLineDetail) (window as any).openLineDetail(alert.machine_id);
                        },
                    });
                    return;
                }
                const mid = alert?.machine_id || item.querySelector('.pa-machine')?.textContent?.trim();
                if (mid) {
                    goToOverviewDetail(mid);
                } else {
                    showSection('sec-overview');
                }
            });
        });
    }
});

document.getElementById('pa-close-btn')?.addEventListener('click', (e) => {
    e.stopPropagation();
    _closeProactivePanel();
});

document.addEventListener('click', (e: MouseEvent) => {
    const panel = document.getElementById('proactive-alerts-panel');
    const btn = document.getElementById('proactive-alerts-btn');
    const t = e.target as Node | null;
    if (panel && !panel.classList.contains('hidden') && !panel.contains(t) && !btn?.contains(t)) {
        _closeProactivePanel();
    }
});

document.getElementById('btn-open-login')?.addEventListener('click', async () => {
    const errMsg = document.getElementById('login-error-msg');
    if (errMsg) { errMsg.style.display = 'none'; errMsg.textContent = ''; }
    document.getElementById('modal-login')?.classList.remove('hidden');
    try {
        const usernames = await api('/api/auth/usernames');
        const dl = document.getElementById('login-usernames-list');
        if (dl && Array.isArray(usernames)) {
            dl.innerHTML = usernames.map(u => `<option value="${escHtml(u)}">`).join('');
        }
    } catch (e) { /* ignore */ }
});

document.getElementById('login-toggle-pwd')?.addEventListener('click', () => {
    const inp = document.getElementById('login-password') as HTMLInputElement | null;
    if (!inp) return;
    const show = inp.type === 'password';
    inp.type = show ? 'text' : 'password';
    const toggle = document.getElementById('login-toggle-pwd');
    if (toggle) {
        toggle.textContent = show ? '🔒' : '👁';
        toggle.title = show ? 'Ascunde parola' : 'Arată parola';
    }
});

export async function loadUsersModal(): Promise<void> {
    const listEl = document.getElementById('users-list');
    if (!listEl) return;
    try {
        const users = await api('/api/users');
        if (!Array.isArray(users)) {
            listEl.innerHTML = '<p style="color:var(--red)">Trebuie să fii autentificat ca Master.</p>';
            return;
        }
        const roleLabels: Record<string, string> = { operator: 'Operator', engineer: 'Inginer', master: 'Master' };
        listEl.innerHTML = users.map((u: any) => {
            const shell = u.home_shell === 'hub' ? 'hub' : 'dashboard';
            return `
            <div class="user-row" data-id="${u.id}" style="display:flex;flex-wrap:wrap;align-items:center;gap:.5rem;padding:.4rem 0;border-bottom:1px solid var(--border)">
                <span style="flex:1;min-width:100px;font-weight:600">${escHtml(u.username)}</span>
                <select class="user-role-select" data-id="${u.id}" style="font-size:.85rem;padding:.2rem .4rem">
                    <option value="operator" ${u.role==='operator'?'selected':''}>Operator</option>
                    <option value="engineer" ${u.role==='engineer'?'selected':''}>Inginer</option>
                    <option value="master" ${u.role==='master'?'selected':''}>Master</option>
                </select>
                <select class="user-shell-select" data-id="${u.id}" title="Ecran la logare" style="font-size:.8rem;padding:.2rem .4rem;max-width:140px">
                    <option value="dashboard" ${shell==='dashboard'?'selected':''}>→ Dashboard</option>
                    <option value="hub" ${shell==='hub'?'selected':''}>→ Hub 3D</option>
                </select>
                <input type="password" class="user-pwd-input" data-id="${u.id}" placeholder="Parolă nouă" style="width:100px;font-size:.8rem;padding:.2rem">
                <button class="btn btn-sm" data-save-user="${u.id}">Salvează</button>
                ${users.length > 1 ? `<button class="btn btn-sm btn-red" data-del-user="${u.id}">Șterge</button>` : ''}
            </div>`;
        }).join('');
        listEl.querySelectorAll('[data-save-user]').forEach((btn: Element) => {
            const hb = btn as HTMLElement;
            btn.addEventListener('click', async () => {
                const id = parseInt(hb.dataset.saveUser || '0', 10);
                const role = (listEl.querySelector(`.user-role-select[data-id="${id}"]`) as HTMLSelectElement | null)?.value;
                const homeShell = (listEl.querySelector(`.user-shell-select[data-id="${id}"]`) as HTMLSelectElement | null)?.value;
                const pwd = (listEl.querySelector(`.user-pwd-input[data-id="${id}"]`) as HTMLInputElement | null)?.value?.trim();
                try {
                    await fetch(`/api/users/${id}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            role: role || undefined,
                            home_shell: homeShell || 'dashboard',
                            password: pwd || undefined,
                        }),
                        credentials: 'include'
                    });
                    toast('Salvat');
                    const pwdInp = listEl.querySelector(`.user-pwd-input[data-id="${id}"]`) as HTMLInputElement | null;
                    if (pwdInp) pwdInp.value = '';
                } catch (e: unknown) {
                    toast('Eroare: ' + (e instanceof Error ? e.message : String(e)), 'error');
                }
            });
        });
        listEl.querySelectorAll('[data-del-user]').forEach((btn: Element) => {
            const hb = btn as HTMLElement;
            btn.addEventListener('click', async () => {
                if (!await askConfirm('Ștergi utilizatorul?')) return;
                try {
                    await fetch(`/api/users/${hb.dataset.delUser}`, { method: 'DELETE', credentials: 'include' });
                    toast('Șters');
                    loadUsersModal();
                } catch (e: unknown) {
                    toast('Eroare: ' + (e instanceof Error ? e.message : String(e)), 'error');
                }
            });
        });
        listEl.querySelectorAll('.user-role-select').forEach((sel: Element) => {
            sel.addEventListener('change', () => { /* enable save */ });
        });
    } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : String(e);
        listEl.innerHTML = '<p style="color:var(--red)">Eroare încărcare: ' + escHtml(msg) + '</p>';
    }
}

/**
 * Open user management on the Hub (single source of truth).
 *
 * Strategy A+B (PLATFORM_CONTRACT v1.5 #28): the `users` table is shared between
 * Mentenanță and Hub, with Hub as authoritative owner. Instead of duplicating
 * admin UI here, we delegate to https://hub.cpp-romania.com/admin/users.
 *
 * - Embedded in Hub iframe (`__EMBED_HUB__`):
 *     postMessage to the parent shell so it routes within its SPA without
 *     spawning a new tab. Format mirrors hub→child bus (`type:'navigate'`,
 *     see `wireHubBus()` in main.ts:647).
 * - Standalone (direct access at http://5.9.50.171/, dev, or Hub down):
 *     open Hub admin in a new tab (`noopener` for security).
 *
 * The legacy local modal (#modal-users + loadUsersModal) is kept deprecated as
 * an offline fallback — see templates/index.html:2218.
 */
export function _openHubAdminUsers(): void {
    const HUB_ORIGIN = 'https://hub.cpp-romania.com';
    const HUB_ADMIN_URL = `${HUB_ORIGIN}/admin/users`;
    const w = window as unknown as { __EMBED_HUB__?: boolean };
    if (w.__EMBED_HUB__ === true) {
        try {
            window.parent.postMessage(
                { type: 'navigate', target: '/admin/users', from: 'mentenanta' },
                HUB_ORIGIN
            );
            return;
        } catch (_e) {
            /* parent inaccessible — fall through to new-tab open */
        }
    }
    window.open(HUB_ADMIN_URL, '_blank', 'noopener');
}

(window as unknown as { _openHubAdminUsers?: () => void })._openHubAdminUsers = _openHubAdminUsers;

document.getElementById('btn-open-users')?.addEventListener('click', () => {
    _openHubAdminUsers();
});

document.getElementById('btn-add-user')?.addEventListener('click', async () => {
    const name = (document.getElementById('new-user-name') as HTMLInputElement | null)?.value?.trim();
    const pwd = (document.getElementById('new-user-password') as HTMLInputElement | null)?.value || '';
    const role = (document.getElementById('new-user-role') as HTMLSelectElement | null)?.value || 'operator';
    const homeShell = (document.getElementById('new-user-home-shell') as HTMLSelectElement | null)?.value || 'dashboard';
    if (!name) { toast('Introdu numele utilizatorului', 'error'); return; }
    if (pwd.length < 8) { toast('Parola minim 8 caractere', 'error'); return; }
    try {
        const res = await fetch('/api/users', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username: name, password: pwd, role, home_shell: homeShell }),
            credentials: 'include'
        });
        const d = await res.json().catch(() => ({}));
        if (!res.ok) { toast(d.error || 'Eroare', 'error'); return; }
        toast('Utilizator adăugat');
        const nameEl = document.getElementById('new-user-name') as HTMLInputElement | null;
        const pwdEl = document.getElementById('new-user-password') as HTMLInputElement | null;
        if (nameEl) nameEl.value = '';
        if (pwdEl) pwdEl.value = '';
        loadUsersModal();
    } catch (e: unknown) {
        toast('Eroare: ' + (e instanceof Error ? e.message : String(e)), 'error');
    }
});

let _pending2faUser = '';
let _pending2faPwd = '';

async function _doLogin(user: string, pwd: string, totpCode: string = '') {
    const errMsg = document.getElementById('login-error-msg');
    if (errMsg) { errMsg.style.display = 'none'; errMsg.textContent = ''; }

    const body: any = { username: user, password: pwd };
    if (totpCode) body.totp_code = totpCode;

    const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
    });
    const data = await res.json();

    if (!res.ok) {
        const msg = data.error || 'Eroare autentificare';
        if (errMsg) { errMsg.textContent = '\u274c ' + msg; errMsg.style.display = 'block'; }
        if (res.status === 429) return;
        const totpRow = document.getElementById('login-totp-row');
        if (totpRow && !totpRow.classList.contains('hidden')) {
            (document.getElementById('login-totp') as HTMLInputElement)?.focus();
        } else {
            (document.getElementById('login-password') as HTMLInputElement)?.focus();
        }
        return;
    }

    if (data.requires_2fa) {
        _pending2faUser = user;
        _pending2faPwd = pwd;
        const totpRow = document.getElementById('login-totp-row');
        if (totpRow) {
            totpRow.classList.remove('hidden');
            (document.getElementById('login-totp') as HTMLInputElement)?.focus();
        }
        if (errMsg) { errMsg.textContent = '\U0001f510 Introdu codul din aplicația authenticator.'; errMsg.style.display = 'block'; errMsg.style.color = '#4fc3f7'; }
        return;
    }

    _pending2faUser = '';
    _pending2faPwd = '';
    const totpRow = document.getElementById('login-totp-row');
    if (totpRow) totpRow.classList.add('hidden');

    state.currentUser = data;
    const roleLabels: Record<string, string> = { operator: 'Operator', engineer: 'Inginer', master: 'Master' };
    const lbl = document.getElementById('current-role-label');
    if (lbl) lbl.textContent = `Nivel: ${roleLabels[String(data.role)] || data.role}`;
    document.getElementById('modal-login')?.classList.add('hidden');
    _hideAuthGate();
    toast(`Autentificat ca ${roleLabels[String(data.role)] || data.role}`);
    _applyRoleMenus(String(data.role));
    _updateHelpBadge();
    _checkAiLocalStatus();
    _checkAiCloudStatus();
    try {
        const cfg = await (window as any).loadFactoryConfig?.();
        (window as any).applyFactoryTheme?.(cfg);
    } catch (_e) { /* config may not be available for operator */ }
    applyInitialAppRoute();
    _checkClarificationNotifications(data);

    if (data.must_change_password) {
        setTimeout(() => toast('Trebuie să schimbați parola implicită!', 'error'), 500);
    }
}

document.getElementById('btn-login-submit')?.addEventListener('click', async () => {
    const userEl = document.getElementById('login-username') as HTMLInputElement | null;
    const pwdEl = document.getElementById('login-password') as HTMLInputElement | null;
    const totpEl = document.getElementById('login-totp') as HTMLInputElement | null;
    if (!userEl || !pwdEl) return;

    if (_pending2faUser && totpEl && totpEl.value.trim()) {
        try {
            await _doLogin(_pending2faUser, _pending2faPwd, totpEl.value.trim());
        } catch (e: unknown) {
            const errMsg = document.getElementById('login-error-msg');
            if (errMsg) { errMsg.textContent = '\u274c Eroare conexiune'; errMsg.style.display = 'block'; }
        }
        return;
    }

    const user = userEl.value.trim();
    const pwd = pwdEl.value || user;
    if (!user) {
        const errMsg = document.getElementById('login-error-msg');
        if (errMsg) { errMsg.textContent = '\u26a0 Introdu numele de utilizator.'; errMsg.style.display = 'block'; }
        return;
    }
    try {
        await _doLogin(user, pwd);
    } catch (e: unknown) {
        const errMsg = document.getElementById('login-error-msg');
        if (errMsg) {
            errMsg.textContent = '\u274c Eroare conexiune: ' + (e instanceof Error ? e.message : String(e));
            errMsg.style.display = 'block';
        }
    }
});

// ── 2FA Setup UI ────────────────────────────────────────────
document.getElementById('btn-open-2fa')?.addEventListener('click', async () => {
    const modal = document.getElementById('modal-2fa-setup');
    if (!modal) return;
    modal.classList.remove('hidden');
    const qrC = document.getElementById('2fa-qr-container');
    const activeC = document.getElementById('2fa-active-info');
    const startC = document.getElementById('2fa-setup-start');
    const recovC = document.getElementById('2fa-recovery-codes');
    [qrC, activeC, startC, recovC].forEach(el => el?.classList.add('hidden'));

    try {
        const status = await api('/api/auth/2fa/status');
        if (status.totp_enabled) {
            activeC?.classList.remove('hidden');
        } else {
            startC?.classList.remove('hidden');
        }
    } catch { startC?.classList.remove('hidden'); }
});

document.getElementById('btn-2fa-start')?.addEventListener('click', async () => {
    try {
        const res = await api('/api/auth/2fa/setup', { method: 'POST' });
        const qrImg = document.getElementById('2fa-qr-img') as HTMLImageElement;
        if (qrImg) qrImg.src = res.qr_code;
        const secretTxt = document.getElementById('2fa-secret-text');
        if (secretTxt) secretTxt.textContent = `Secret: ${res.secret}`;
        document.getElementById('2fa-setup-start')?.classList.add('hidden');
        document.getElementById('2fa-qr-container')?.classList.remove('hidden');
        (document.getElementById('2fa-verify-code') as HTMLInputElement)?.focus();
    } catch (e: unknown) { toast('Eroare setup 2FA: ' + (e instanceof Error ? e.message : String(e)), 'error'); }
});

document.getElementById('btn-2fa-verify')?.addEventListener('click', async () => {
    const codeEl = document.getElementById('2fa-verify-code') as HTMLInputElement;
    if (!codeEl || !codeEl.value.trim()) { toast('Introduceți codul de 6 cifre', 'error'); return; }
    try {
        const res = await api('/api/auth/2fa/verify', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code: codeEl.value.trim() })
        });
        document.getElementById('2fa-qr-container')?.classList.add('hidden');
        if (res.recovery_codes) {
            const list = document.getElementById('2fa-codes-list');
            if (list) list.innerHTML = res.recovery_codes.map((c: string) => `<div>${c}</div>`).join('');
            document.getElementById('2fa-recovery-codes')?.classList.remove('hidden');
        }
        toast('2FA activat cu succes!');
    } catch (e: unknown) { toast('Cod invalid: ' + (e instanceof Error ? e.message : String(e)), 'error'); }
});

document.getElementById('btn-2fa-disable')?.addEventListener('click', async () => {
    if (!await askConfirm('Sigur doriți să dezactivați 2FA?')) return;
    try {
        await api('/api/auth/2fa/disable', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
        toast('2FA dezactivat');
        document.getElementById('modal-2fa-setup')?.classList.add('hidden');
    } catch (e: unknown) { toast('Eroare: ' + (e instanceof Error ? e.message : String(e)), 'error'); }
});

// ── Change Password UI ──────────────────────────────────────
// Tema D — doar masterul vede comutatorul (vezi tema.ts)
document.getElementById('tema-comutator')?.addEventListener('click', () => comutaTema());

document.getElementById('btn-open-chpwd')?.addEventListener('click', () => {
    document.getElementById('modal-change-password')?.classList.remove('hidden');
    (document.getElementById('chpwd-old') as HTMLInputElement).value = '';
    (document.getElementById('chpwd-new') as HTMLInputElement).value = '';
    (document.getElementById('chpwd-confirm') as HTMLInputElement).value = '';
    const err = document.getElementById('chpwd-error');
    if (err) err.style.display = 'none';
});

document.getElementById('btn-chpwd-submit')?.addEventListener('click', async () => {
    const oldPwd = (document.getElementById('chpwd-old') as HTMLInputElement)?.value || '';
    const newPwd = (document.getElementById('chpwd-new') as HTMLInputElement)?.value || '';
    const confirmPwd = (document.getElementById('chpwd-confirm') as HTMLInputElement)?.value || '';
    const errEl = document.getElementById('chpwd-error');

    if (newPwd.length < 8) {
        if (errEl) { errEl.textContent = 'Parola nouă trebuie să aibă minim 8 caractere'; errEl.style.display = 'block'; }
        return;
    }
    if (newPwd !== confirmPwd) {
        if (errEl) { errEl.textContent = 'Parolele nu coincid'; errEl.style.display = 'block'; }
        return;
    }
    try {
        await api('/api/auth/change_password', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ old_password: oldPwd, new_password: newPwd })
        });
        toast('Parola a fost schimbată cu succes');
        document.getElementById('modal-change-password')?.classList.add('hidden');
    } catch (e: unknown) {
        if (errEl) { errEl.textContent = (e instanceof Error ? e.message : String(e)); errEl.style.display = 'block'; }
    }
});

export async function _checkClarificationNotifications(userData: any): Promise<void> {
    try {
        const logs = await api('/api/maintenance_log?validation_status=needs_clarification');
        if (!Array.isArray(logs) || logs.length === 0) return;

        const username = (userData.display_name || userData.username || '').toLowerCase();
        const isMasterOrEngineer = userData.role === 'master' || userData.role === 'engineer';

        const myLogs = isMasterOrEngineer
            ? logs
            : logs.filter(l => (l.technician || '').toLowerCase() === username);

        if (myLogs.length === 0) return;

        const label = isMasterOrEngineer
            ? `${myLogs.length} intervenție(i) trimise la clarificare`
            : `${myLogs.length} intervenție(i) necesită completarea ta`;

        const existing = document.getElementById('clarify-notification-bar');
        if (existing) existing.remove();

        const notifDiv = document.createElement('div');
        notifDiv.id = 'clarify-notification-bar';
        notifDiv.style.cssText = 'position:fixed;top:50px;left:170px;right:16px;z-index:10000;padding:14px 20px;border-radius:10px;background:linear-gradient(135deg,#f59e0b,#d97706);color:#000;font-weight:600;font-size:.95rem;box-shadow:0 4px 20px rgba(0,0,0,.3);display:flex;align-items:center;gap:10px;animation:slideDown .4s ease-out';
        notifDiv.innerHTML = `
            <span style="font-size:1.4rem">🔄</span>
            <span style="flex:1">${escHtml(label)}</span>
            <button id="clarify-notif-action" style="padding:6px 16px;border-radius:6px;border:2px solid #000;background:rgba(0,0,0,.15);color:#000;font-weight:700;font-size:.9rem;cursor:pointer">📋 Vezi detalii</button>
            <button id="clarify-notif-close" style="padding:4px 10px;border:none;background:transparent;color:rgba(0,0,0,.5);font-size:1.2rem;cursor:pointer;line-height:1" title="Închide">&times;</button>`;

        document.body.appendChild(notifDiv);

        document.getElementById('clarify-notif-action')!.addEventListener('click', () => {
            if (isMasterOrEngineer) {
                showSection('sec-reports');
            } else {
                showSection('sec-journal');
                setTimeout(() => {
                    urgShowPhase('urg-phase-history');
                    loadJournalLog();
                }, 300);
            }
        });

        document.getElementById('clarify-notif-close')!.addEventListener('click', () => {
            notifDiv.remove();
        });
    } catch(_e: unknown) {}
}

export function _buildTreeSectionOptions(sections: any[], includeEmpty: boolean): string {
    const roots = sections.filter((s: any) => !s.parent_id).sort((a: any, b: any) => {
        const na = parseFloat(a.section_number) || a.sort_order || 0;
        const nb = parseFloat(b.section_number) || b.sort_order || 0;
        return na - nb;
    });
    const childrenOf = (pid: any) => sections.filter((s: any) => s.parent_id === pid).sort((a: any, b: any) => {
        const na = parseFloat(a.section_number) || a.sort_order || 0;
        const nb = parseFloat(b.section_number) || b.sort_order || 0;
        return na - nb;
    });
    let html = includeEmpty ? '<option value="">-- Fara alocare --</option>' : '';
    function addNode(s: any, depth: number): void {
        const indent = '&nbsp;&nbsp;&nbsp;'.repeat(depth);
        const num = s.section_number ? s.section_number + ' ' : '';
        html += `<option value="${s.id}">${indent}${num}${escHtml(s.title)}</option>`;
        childrenOf(s.id).forEach((ch: any) => addNode(ch, depth + 1));
    }
    roots.forEach((r: any) => addNode(r, 0));
    return html;
}

export function _renderDocumentTemplateNavigator(d: any): string {
    const tpl = d.template;
    const sections = d.template_sections || [];
    const chapters = d.document_chapters || [];
    const docs = d.documents || [];
    const machineDocs = d.machine_documents || [];

    const sectionMap: Record<string, any> = {};
    sections.forEach((s: any) => { sectionMap[s.id] = s; });

    const filledSectionIds = new Set();
    machineDocs.forEach((md: any) => { if (md.template_section_id) filledSectionIds.add(md.template_section_id); });

    const rootSections = sections.filter((s: any) => !s.parent_id);
    const childrenOf = (parentId: any) => sections.filter((s: any) => s.parent_id === parentId).sort((a: any, b: any) => a.sort_order - b.sort_order);
    const isLeaf = (s: any) => childrenOf(s.id).length === 0;

    let filledCount: number, totalCount: number;
    if (tpl.template_type === 'categories') {
        totalCount = rootSections.length;
        filledCount = rootSections.filter((s: any) => filledSectionIds.has(s.id)).length;
    } else {
        const leafSections = sections.filter((s: any) => isLeaf(s));
        totalCount = leafSections.length;
        filledCount = leafSections.filter((s: any) => filledSectionIds.has(s.id)).length;
    }
    const pct = totalCount > 0 ? Math.round(filledCount / totalCount * 100) : 0;

    let html = '<div class="doc-template-nav">';
    html += `<div class="doc-completeness">
        <div class="doc-completeness-header">
            <span class="doc-completeness-label">${escHtml(tpl.name)}</span>
            <span class="doc-completeness-pct">${pct}% complet (${filledCount}/${totalCount})</span>
        </div>
        <div class="doc-completeness-bar"><div class="doc-completeness-fill" style="width:${pct}%"></div></div>
    </div>`;

    if (tpl.template_type === 'categories') {
        html += _renderCategoriesView(rootSections, filledSectionIds, docs, machineDocs, chapters);
    } else {
        html += _renderTreeView(rootSections, childrenOf, filledSectionIds, docs, chapters, machineDocs);
    }

    if (tpl.template_type !== 'categories' && chapters.length > 0) {
        html += _renderDetectedChaptersList(chapters, docs);
    }

    const isMaster = (state.currentUser && state.currentUser.role === 'master');
    if (isMaster && tpl.template_type !== 'categories' && docs.length > 0) {
        const docOptions = docs.map((dc: any) => `<option value="${dc.id}">${escHtml(dc.filename)}</option>`).join('');
        const secOptions = _buildTreeSectionOptions(sections, false);
        html += `<div class="doc-add-chapter">
            <div class="doc-add-chapter-title">Adauga capitol manual</div>
            <div class="doc-add-chapter-form">
                <select class="doc-add-doc-select">${docOptions}</select>
                <select class="doc-add-sec-select"><option value="">-- sectiune template --</option>${secOptions}</select>
                <input type="text" class="doc-add-ch-num" placeholder="Nr. (ex: 3.1)" style="width:70px">
                <input type="text" class="doc-add-ch-title" placeholder="Titlu capitol">
                <input type="number" class="doc-add-ch-start" placeholder="Pag. start" style="width:80px">
                <input type="number" class="doc-add-ch-end" placeholder="Pag. sfarsit" style="width:80px">
                <button type="button" class="btn btn-sm doc-add-ch-btn" style="background:rgba(16,185,129,.15);border-color:rgba(16,185,129,.4);color:#10b981">Adauga</button>
            </div>
        </div>`;
    }

    html += '</div>';
    return html;
}

export function _renderTreeView(rootSections: any[], childrenOf: (id: any) => any[], filledSectionIds: Set<any>, docs: any[], chapters: any[], machineDocs: any[]): string {
    let html = '<div class="doc-tree">';
    rootSections.sort((a: any, b: any) => a.sort_order - b.sort_order);
    rootSections.forEach((section: any) => {
        html += _renderTreeNode(section, childrenOf, filledSectionIds, docs, chapters, machineDocs, 0);
    });
    html += '</div>';
    return html;
}

export function _renderTreeNode(section: any, childrenOf: (id: any) => any[], filledSectionIds: Set<any>, docs: any[], chapters: any[], machineDocs: any[], depth: number): string {
    const children = childrenOf(section.id);
    const hasChildren = children.length > 0;
    const isFilled = filledSectionIds.has(section.id);
    const isLeaf = !hasChildren;
    const isMaster = (state.currentUser && state.currentUser.role === 'master');

    const statusIcon = isLeaf ? (isFilled ? '<span class="doc-tree-status filled"></span>' : '<span class="doc-tree-status empty"></span>') : '';
    const expandIcon = hasChildren || isFilled ? '<span class="doc-tree-expand">&#9654;</span>' : '<span class="doc-tree-expand-placeholder"></span>';
    const numLabel = section.section_number ? `<span class="doc-tree-num">${escHtml(section.section_number)}</span>` : '';

    const sectionDocs = (machineDocs || []).filter((md: any) => md.template_section_id === section.id);
    let chapterInfo = '';

    if (sectionDocs.length > 0) {
        const docIds = sectionDocs.map((md: any) => md.document_id);
        const docChapters = chapters.filter((ch: any) => docIds.includes(ch.document_id));
        if (docChapters.length > 0) {
            const minPage = Math.min(...docChapters.map((ch: any) => ch.start_page ?? 9999));
            const maxPage = Math.max(...docChapters.map((ch: any) => ch.end_page ?? 0));
            const firstCh = docChapters.find((ch: any) => ch.start_page === minPage);
            if (firstCh && minPage < 9999) {
                chapterInfo += `<span class="doc-tree-page" data-doc-id="${firstCh.document_id}" data-page="${minPage + 1}">p.${minPage + 1}${maxPage > minPage ? '-' + (maxPage + 1) : ''}</span>`;
            }
        }
        if (isMaster) {
            sectionDocs.forEach((md: any) => {
                chapterInfo += `<span class="doc-ch-delete" data-md-id="${md.id}" title="Sterge alocarea documentului">&times;</span>`;
            });
        }
    }

    if (isLeaf && isMaster && !isFilled && docs.length > 0) {
        let allocOpts = docs.map((dc: any) => {
            const fn = (dc.filename || '').split('_').slice(1).join('_') || dc.filename;
            return `<option value="${dc.id}">${escHtml(fn)}</option>`;
        }).join('');
        chapterInfo += `<select class="doc-tree-alloc" data-section-id="${section.id}"><option value="">Aloca...</option>${allocOpts}</select>`;
    }

    let html = `<div class="doc-tree-item depth-${depth}${hasChildren || isFilled ? ' has-children' : ''}${isFilled ? ' is-filled' : ''}" data-section-id="${section.id}">
        <div class="doc-tree-row">${expandIcon}${statusIcon}${numLabel}<span class="doc-tree-title">${escHtml(section.title)}</span>${chapterInfo}</div>`;

    if (hasChildren || isFilled) {
        html += `<div class="doc-tree-children" style="display:none">`;
        children.forEach((child: any) => {
            html += _renderTreeNode(child, childrenOf, filledSectionIds, docs, chapters, machineDocs, depth + 1);
        });
        if (sectionDocs.length > 0) {
            const docIds = sectionDocs.map(md => md.document_id);
            const docChapters = chapters.filter(ch => docIds.includes(ch.document_id));
            if (docChapters.length > 0) {
                html += '<div class="doc-section-chapters">';
                docChapters.sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0));
                docChapters.forEach((ch: any) => {
                    const pageLabel = ch.start_page != null ? `p.${ch.start_page + 1}${ch.end_page != null && ch.end_page !== ch.start_page ? '-' + (ch.end_page + 1) : ''}` : '';
                    html += `<div class="doc-section-ch-item" style="padding-left:${(depth+1)*18+12}px">
                        <span class="doc-ch-num">${escHtml(ch.chapter_number || '')}</span>
                        <span class="doc-ch-title">${escHtml(ch.title || '')}</span>
                        <span class="doc-tree-page" data-doc-id="${ch.document_id}" data-page="${(ch.start_page || 0) + 1}">${pageLabel}</span>
                    </div>`;
                });
                html += '</div>';
            }
        }
        html += '</div>';
    }
    html += '</div>';
    return html;
}

export function _renderDetectedChaptersList(chapters: any[], docs: any[]): string {
    const detected = chapters.filter((ch: any) => ch.auto_detected);
    if (detected.length === 0) return '';
    const isMaster = (state.currentUser && state.currentUser.role === 'master');

    const byDoc: Record<string, any[]> = {};
    detected.forEach((ch: any) => {
        if (!byDoc[ch.document_id]) byDoc[ch.document_id] = [];
        byDoc[ch.document_id].push(ch);
    });

    let html = '<div class="doc-detected-chapters">';
    html += `<div class="doc-detected-header">Capitole detectate automat (${detected.length})</div>`;
    html += '<div class="doc-detected-list">';
    Object.keys(byDoc).forEach(docId => {
        const doc = docs.find((d: any) => d.id === parseInt(docId, 10));
        const docName = doc ? doc.filename.split('_').slice(1).join('_') : `doc#${docId}`;
        html += `<div class="doc-detected-doc-header">${escHtml(docName)} (${byDoc[docId].length} capitole)</div>`;
        byDoc[docId].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        byDoc[docId].forEach((ch: any) => {
            html += `<div class="doc-detected-item">
                <span class="doc-ch-num">${escHtml(ch.chapter_number || '')}</span>
                <span class="doc-ch-title">${escHtml(ch.title || '')}</span>
                <span class="doc-tree-page" data-doc-id="${ch.document_id}" data-page="${(ch.start_page || 0) + 1}">p.${ch.start_page != null ? ch.start_page + 1 : '?'}-${ch.end_page != null ? ch.end_page + 1 : '?'}</span>
                ${isMaster ? `<span class="doc-ch-delete" data-ch-id="${ch.id}" data-doc-id="${ch.document_id}" title="Sterge">&times;</span>` : ''}
            </div>`;
        });
    });
    html += '</div></div>';
    return html;
}

export function _renderCategoriesView(rootSections: any[], filledSectionIds: Set<any>, docs: any[], machineDocs: any[], chapters: any[]): string {
    const isMaster = (state.currentUser && state.currentUser.role === 'master');
    const allChapters = chapters || [];
    let html = '<div class="doc-categories">';
    rootSections.sort((a, b) => a.sort_order - b.sort_order);
    rootSections.forEach((cat: any) => {
        const catDocs = machineDocs.filter((md: any) => md.template_section_id === cat.id);
        const isFilled = catDocs.length > 0;
        html += `<div class="doc-category">
            <div class="doc-category-header">
                <span class="doc-category-title">${escHtml(cat.title)}</span>
                <span class="doc-category-count">${catDocs.length} doc${catDocs.length !== 1 ? '.' : '.'}</span>
                ${isMaster ? `<button type="button" class="btn btn-xs doc-cat-upload-btn" data-section-id="${cat.id}" title="Adauga document in aceasta categorie">+</button>` : ''}
            </div>
            <div class="doc-category-items">`;
        if (catDocs.length === 0) {
            html += `<div class="doc-category-item empty"><span class="doc-cat-status empty"></span><span class="doc-cat-title" style="color:var(--text-muted)">Niciun document</span></div>`;
        }
        catDocs.forEach((md: any) => {
            const desc = md.description || md.doc_type || '';
            const docCode = md.notes || '';
            const fname = (md.filename || '').split('_').slice(1).join('_') || md.filename;
            const docChapters = allChapters.filter((ch: any) => ch.document_id === md.document_id);
            const mainCh = docChapters.filter((ch: any) => {
                const num = ch.chapter_number || '';
                return num && !num.includes('.'); // top-level: "1", "2", etc.
            });
            const hasChapters = docChapters.length > 0;
            const toggleId = `doc-ch-toggle-${md.id}`;

            html += `<div class="doc-category-item filled">
                <span class="doc-cat-status filled"></span>
                <span class="doc-cat-title doc-cat-open" data-filename="${escHtml(md.filename || '')}" style="cursor:pointer">${escHtml(desc || fname)}</span>
                ${docCode ? `<span class="doc-cat-code">${escHtml(docCode)}</span>` : ''}
                <span class="doc-cat-lang">${escHtml(md.language || '')}</span>
                <span class="doc-cat-pages">${md.pages ? md.pages + 'p' : ''}</span>
                ${hasChapters ? `<span class="doc-cat-ch-toggle" data-toggle="${toggleId}" title="Afiseaza capitole">${mainCh.length || docChapters.length} cap.</span>` : ''}
                ${!hasChapters && isMaster && md.pages > 5 ? `<button type="button" class="btn btn-xs doc-cat-detect-ch" data-doc-id="${md.document_id}" title="Detecteaza capitole din PDF" style="font-size:.7rem;padding:0 5px;background:rgba(16,185,129,.1);border:1px solid rgba(16,185,129,.25);color:#10b981">Capitole</button>` : ''}
                ${isMaster ? `<span class="doc-cat-remove" data-md-id="${md.id}" title="Sterge document" data-doc-id="${md.document_id}" data-desc="${escHtml(desc || fname)}">&times;</span>` : ''}
            </div>`;

            if (hasChapters) {
                html += `<div class="doc-ch-list hidden" id="${toggleId}">`;
                if (mainCh.length > 0) {
                    mainCh.forEach((ch: any) => {
                        const sub = docChapters.filter((s: any) => s.parent_id === ch.id);
                        const chP = (ch.start_page || 0) + 1;
                        const chPEnd = ch.end_page != null ? ch.end_page + 1 : chP;
                        html += `<div class="doc-ch-main">
                            <span class="doc-ch-num-link" data-doc-id="${ch.document_id}" data-page="${chP}" title="Deschide la p.${chP}">${escHtml(ch.chapter_number)}</span>
                            <span class="doc-ch-title-link" data-doc-id="${ch.document_id}" data-page="${chP}" title="Deschide la p.${chP}">${escHtml(ch.title)}</span>
                            <span class="doc-ch-pages">p.${chP}${chPEnd > chP ? '-' + chPEnd : ''}</span>
                            ${isMaster ? `<span class="doc-ch-del" data-ch-id="${ch.id}" data-doc-id="${ch.document_id}" title="Sterge capitol">&times;</span>` : ''}
                        </div>`;
                        if (sub.length > 0) {
                            sub.forEach((s: any) => {
                                const sP = (s.start_page || 0) + 1;
                                const sPEnd = s.end_page != null ? s.end_page + 1 : sP;
                                html += `<div class="doc-ch-sub">
                                    <span class="doc-ch-num-link" data-doc-id="${s.document_id}" data-page="${sP}" title="Deschide la p.${sP}">${escHtml(s.chapter_number)}</span>
                                    <span class="doc-ch-title-link" data-doc-id="${s.document_id}" data-page="${sP}" title="Deschide la p.${sP}">${escHtml(s.title)}</span>
                                    <span class="doc-ch-pages">p.${sP}${sPEnd > sP ? '-' + sPEnd : ''}</span>
                                    ${isMaster ? `<span class="doc-ch-del" data-ch-id="${s.id}" data-doc-id="${s.document_id}" title="Sterge capitol">&times;</span>` : ''}
                                </div>`;
                            });
                        }
                    });
                    const unparented = docChapters.filter((ch: any) => !ch.parent_id && (!ch.chapter_number || ch.chapter_number.includes('.')));
                    if (unparented.length > 0 && mainCh.length > 0) {
                        // deep chapters without parents shown collapsed
                    }
                } else {
                    docChapters.forEach((ch: any) => {
                        const titleFirst = (ch.title || '').split('\n')[0];
                        const chP2 = (ch.start_page || 0) + 1;
                        const chPEnd2 = ch.end_page != null ? ch.end_page + 1 : chP2;
                        html += `<div class="doc-ch-main">
                            <span class="doc-ch-num-link" data-doc-id="${ch.document_id}" data-page="${chP2}" title="Deschide la p.${chP2}">${escHtml(ch.chapter_number || '-')}</span>
                            <span class="doc-ch-title-link" data-doc-id="${ch.document_id}" data-page="${chP2}" title="Deschide la p.${chP2}">${escHtml(titleFirst)}</span>
                            <span class="doc-ch-pages">p.${chP2}${chPEnd2 > chP2 ? '-' + chPEnd2 : ''}</span>
                            ${isMaster ? `<span class="doc-ch-del" data-ch-id="${ch.id}" data-doc-id="${ch.document_id}" title="Sterge capitol">&times;</span>` : ''}
                        </div>`;
                    });
                }
                if (isMaster) {
                    html += `<div class="doc-ch-add-row">
                        <input type="text" class="doc-ch-add-num" placeholder="Nr." style="width:50px" data-doc-id="${md.document_id}">
                        <input type="text" class="doc-ch-add-title" placeholder="Titlu capitol" data-doc-id="${md.document_id}">
                        <input type="number" class="doc-ch-add-start" placeholder="De la p." style="width:65px">
                        <input type="number" class="doc-ch-add-end" placeholder="La p." style="width:65px">
                        <button type="button" class="btn btn-xs doc-ch-add-btn" data-doc-id="${md.document_id}" title="Adauga capitol">+</button>
                    </div>`;
                }
                html += '</div>';
            }
        });
        html += '</div></div>';
    });
    html += '</div>';
    return html;
}

/** La deschiderea tab-ului Documente: dacă un document e deja în curs de procesare pe server, afișează progresul și pornește polling. */
export function refreshDocumentProcessingStatus(): void {
    const allBtns = document.querySelectorAll('.doc-process-btn, .doc-reprocess-btn');
    if (!allBtns.length) return;
    (window as any)._docWasProcessing = (window as any)._docWasProcessing || {};
    allBtns.forEach((btn: Element) => {
        const docId = btn.getAttribute('data-doc-id');
        if (!docId) return;
        const statusEl = document.getElementById('doc-status-' + docId);
        const btnEl = btn as HTMLButtonElement;
        (async function poll() {
            try {
                const r = await fetch(`/api/documents/${docId}/process_status`);
                const st = await r.json();
                if (st && st.processing) {
                    (window as any)._docWasProcessing[docId] = true;
                    const stageMap: Record<string, string> = { extracting: '📄 Extragere text', vision: '🖼️ AI Vision', ai: '🤖 Analiză AI', saved: '💾 Lot salvat', ai_esuat: '⚠️ Lot eșuat (se reia)' };
                    const stageLabel = stageMap[String(st.stage).replace(/^reprocess_/, '')] || st.stage || '';
                    if (statusEl) statusEl.textContent = st.total_pages > 0
                        ? `${stageLabel} ${st.pages_extracted || 0}/${st.total_pages} pag.`
                        : 'Se procesează…';
                    btnEl.disabled = true;
                    (window as any)._docProgressPoll = (window as any)._docProgressPoll || {};
                    if (!(window as any)._docProgressPoll[docId]) {
                        (window as any)._docProgressPoll[docId] = setInterval(poll, 3000);
                    }
                    return;
                }
                if (st && st.queued) {
                    (window as any)._docWasProcessing[docId] = true;
                    if (statusEl) statusEl.textContent = `⏳ În coadă (poziția ${st.queue_position})`;
                    btnEl.disabled = true;
                    (window as any)._docProgressPoll = (window as any)._docProgressPoll || {};
                    if (!(window as any)._docProgressPoll[docId]) {
                        (window as any)._docProgressPoll[docId] = setInterval(poll, 5000);
                    }
                    return;
                }
                if ((window as any)._docProgressPoll && (window as any)._docProgressPoll[docId]) {
                    clearInterval((window as any)._docProgressPoll[docId]);
                    delete (window as any)._docProgressPoll[docId];
                }
                if ((window as any)._docWasProcessing[docId]) {
                    delete (window as any)._docWasProcessing[docId];
                    if ((window as any)._detailLineId) {
                        Object.keys((window as any)._docProgressPoll || {}).forEach((id: string) => clearInterval((window as any)._docProgressPoll[id]));
                        (window as any)._docProgressPoll = {};
                        const fresh = await fetch(`/api/line_detail/${(window as any)._detailLineId}`).then(x => x.json());
                        (window as any)._detailData = fresh;
                        renderDetailTab();
                    }
                }
            } catch (_) {}
        })();
    });
}

document.addEventListener('DOMContentLoaded', () => {
    loadCurrentUser();
    ensureReportsInNav();
    _initLabelScan('btn-scan-label-add', 'scan-label-file-add', 'scan-label-status-add', 'scan-label-preview-add', 'cfg-eq');
    _initLabelScan('btn-scan-label-edit', 'scan-label-file-edit', 'scan-label-status-edit', 'scan-label-preview-edit', 'edit-eq');
    _initPhotoUpload('btn-add-photo-add', 'add-photo-file-add', 'scan-label-status-add', 'scan-label-preview-add', 'cfg-eq');
    _initPhotoUpload('btn-add-photo-edit', 'add-photo-file-edit', 'scan-label-status-edit', 'scan-label-preview-edit', 'edit-eq');
    document.body.addEventListener('click', async (e: MouseEvent) => {
        const raw = e.target;
        const tgt = raw instanceof Element ? raw : raw instanceof Node ? raw.parentElement : null;
        if (!tgt) return;
        const abandonBtn = tgt.closest('.doc-abandon-btn') as HTMLButtonElement | null;
        if (abandonBtn && !abandonBtn.disabled) {
            e.preventDefault();
            const docId = abandonBtn.getAttribute('data-doc-id');
            if (!docId) return;
            abandonBtn.disabled = true;
            try {
                const res = await fetch(`/api/documents/${docId}/process`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ abandon: true })
                });
                const data = await res.json().catch(() => ({}));
                if (res.ok && data.progress_cleared) {
                    toast('Progres eliberat. Apasă acum «Procesează».');
                    if ((window as any)._detailLineId) { const fresh = await api(`/api/line_detail/${(window as any)._detailLineId}`); (window as any)._detailData = fresh; renderDetailTab(); }
                } else {
                    toast(data.error || 'Eliberarea a eșuat.', 'error');
                }
            } catch (_) {
                toast('Eroare la eliberare.', 'error');
            }
            abandonBtn.disabled = false;
            return;
        }
        const imgBtn = tgt.closest('.doc-extract-imgs-btn') as HTMLButtonElement | null;
        if (imgBtn && !imgBtn.disabled) {
            e.preventDefault();
            const docId = imgBtn.getAttribute('data-doc-id');
            if (!docId) return;
            imgBtn.disabled = true;
            imgBtn.textContent = '⏳ Extragere...';
            try {
                const res = await fetch(`/api/documents/${docId}/extract_images`, { method: 'POST', credentials: 'include' });
                const data = await res.json();
                if (res.ok) {
                    toast(`${data.images_saved || 0} imagini extrase din PDF`, 'success');
                } else {
                    toast(data.error || 'Eroare', 'error');
                }
            } catch (err: unknown) {
                toast('Eroare: ' + (err instanceof Error ? err.message : String(err)), 'error');
            }
            imgBtn.disabled = false;
            imgBtn.textContent = '🖼 Imagini';
            return;
        }
        const analyzeBtn = tgt.closest('.doc-analyze-imgs-btn') as HTMLButtonElement | null;
        if (analyzeBtn && !analyzeBtn.disabled) {
            e.preventDefault();
            const docId = analyzeBtn.getAttribute('data-doc-id');
            if (!docId) return;
            analyzeBtn.disabled = true;
            analyzeBtn.textContent = '⏳ Analiză Vision...';
            try {
                const res = await fetch(`/api/documents/${docId}/analyze_images`, { method: 'POST', credentials: 'include' });
                const data = await res.json();
                if (res.ok) {
                    toast(`${data.images_analyzed || 0} imagini analizate cu AI Vision`, 'success');
                    if ((window as any)._detailLineId) { const fresh = await api(`/api/line_detail/${(window as any)._detailLineId}`); (window as any)._detailData = fresh; renderDetailTab(); }
                } else {
                    toast(data.error || 'Eroare', 'error');
                }
            } catch (err: unknown) {
                toast('Eroare: ' + (err instanceof Error ? err.message : String(err)), 'error');
            }
            analyzeBtn.disabled = false;
            analyzeBtn.textContent = '🔍 Analiză Vision';
            return;
        }
        const delBtn = tgt.closest('.doc-delete-btn') as HTMLButtonElement | null;
        if (delBtn && !delBtn.disabled) {
            e.preventDefault();
            const docId = delBtn.getAttribute('data-doc-id');
            const docName = delBtn.getAttribute('data-doc-name') || '';
            if (!docId) return;
            delBtn.disabled = true;
            try {
                const infoRes = await fetch(`/api/documents/${docId}/info`);
                const info = await infoRes.json();
                let msg = `Sigur vrei să ștergi documentul "${docName}"?`;
                if (info.intervals || info.parts || info.extracts) {
                    msg += `\n\nSe vor șterge și:\n`;
                    if (info.intervals) msg += `• ${info.intervals} intervale de mentenanță\n`;
                    if (info.parts) msg += `• ${info.parts} piese de schimb\n`;
                    if (info.extracts) msg += `• ${info.extracts} proceduri/instrucțiuni extrase\n`;
                }
                msg += '\nAceastă acțiune este ireversibilă.';
                if (!await askConfirm(msg)) { delBtn.disabled = false; return; }
                const res = await fetch(`/api/documents/${docId}`, { method: 'DELETE', credentials: 'include' });
                const data = await res.json().catch(() => ({}));
                if (res.ok) {
                    toast(`Document șters: ${data.deleted?.filename || docName}`);
                    if ((window as any)._detailLineId) {
                        const fresh = await api(`/api/line_detail/${(window as any)._detailLineId}`);
                        (window as any)._detailData = fresh;
                        renderDetailTab();
                    }
                } else {
                    toast(data.error || 'Eroare la ștergere', 'error');
                }
            } catch (err: unknown) {
                toast('Eroare: ' + (err instanceof Error ? err.message : String(err)), 'error');
            }
            delBtn.disabled = false;
            return;
        }
        const btn = tgt.closest('.doc-process-btn, .doc-reprocess-btn') as HTMLButtonElement | null;
        if (!btn || btn.disabled) return;
        const docId = btn.getAttribute('data-doc-id');
        if (!docId) return;
        e.preventDefault();
        const isReprocess = btn.classList.contains('doc-reprocess-btn');
        const statusEl = document.getElementById('doc-status-' + docId);
        const btnLabel = btn.textContent;
        btn.disabled = true;
        if (statusEl) statusEl.textContent = 'Se procesează…';
        let isBackground = false;
        let progressInterval: ReturnType<typeof setInterval> | null = setInterval(async () => {
            try {
                const r = await fetch(`/api/documents/${docId}/process_status`);
                const st = await r.json();
                if (st && st.abandoned) {
                    if (progressInterval != null) clearInterval(progressInterval);
                    progressInterval = null;
                    if (statusEl) statusEl.textContent = 'Întrerupt (timeout)';
                    toast('Procesarea a fost întreruptă (blocare/timeout). Poți reporni cu Procesează.', 'error');
                    btn.disabled = false;
                    setTimeout(() => { if (statusEl) statusEl.textContent = ''; }, 4000);
                    return;
                }
                if (st && st.queued) {
                    if (statusEl) statusEl.textContent = `⏳ În coadă (poziția ${st.queue_position})`;
                    return;
                }
                if (isBackground && (!st || !st.processing)) {
                    if (progressInterval != null) clearInterval(progressInterval);
                    progressInterval = null;
                    isBackground = false;
                    if ((window as any)._detailLineId) {
                        const fresh = await api(`/api/line_detail/${(window as any)._detailLineId}`);
                        (window as any)._detailData = fresh;
                        renderDetailTab();
                    }
                    btn.disabled = false;
                    if (statusEl) statusEl.textContent = '';
                    toast('Procesare finalizată. Verifică statusul documentului.');
                    return;
                }
                if (statusEl && st && st.processing) {
                    const stageMap: Record<string, string> = { extracting: '📄 Extragere text', vision: '🖼️ AI Vision', ai: '🤖 Analiză AI', saved: '💾 Lot salvat', ai_esuat: '⚠️ Lot eșuat (se reia)' };
                    const stageLabel = stageMap[String(st.stage).replace(/^reprocess_/, '')] || st.stage || '';
                    if (st.total_pages > 0) {
                        statusEl.textContent = `${stageLabel} ${st.pages_extracted || 0}/${st.total_pages} pag.`;
                    } else {
                        statusEl.textContent = stageLabel || 'Se procesează…';
                    }
                }
            } catch (_) {}
        }, 2000);
        try {
            const opts: RequestInit = { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include' };
            opts.body = isReprocess ? JSON.stringify({ reprocess: true }) : '{}';
            const res = await fetch(`/api/documents/${docId}/process`, opts);
            const data = await res.json().catch(() => ({}));
            if (res.status === 202 && data.status === 'queued') {
                isBackground = true;
                if (statusEl) statusEl.textContent = `⏳ În coadă (poziția ${data.queue_position || '?'})`;
                toast(data.message || 'Document adăugat în coadă.');
                return;
            }
            if (res.status === 202 && data.progress_started) {
                isBackground = true;
                toast(data.message || 'Procesare pornită în fundal. Statusul se actualizează mai jos.');
                return;
            }
            if (progressInterval != null) clearInterval(progressInterval);
            if (res.ok && data.progress_cleared_retry) {
                toast(data.message || 'Progres eliberat. Apasă din nou «Procesează».');
                if ((window as any)._detailLineId) { const fresh = await api(`/api/line_detail/${(window as any)._detailLineId}`); (window as any)._detailData = fresh; renderDetailTab(); }
                return;
            }
            if (!res.ok) {
                if (res.status === 409) {
                    toast('Procesare blocată. Apasă «Eliberează blocarea» sau încearcă din nou Procesează.', 'error');
                    if ((window as any)._detailLineId) { const fresh = await api(`/api/line_detail/${(window as any)._detailLineId}`); (window as any)._detailData = fresh; renderDetailTab(); }
                    return;
                }
                let errMsg = data.error;
                if (!errMsg) errMsg = res.status === 400 ? 'Cerere invalidă (verifică cheia API în Chat AI → Setări).' : res.status === 429 ? 'Limită API depășită. Așteaptă 1–2 minute și reîncearcă.' : res.status === 500 ? 'Eroare server la procesare. Verifică jurnalul serverului (logs).' : res.status === 503 ? 'Procesare în fundal indisponibilă. Repornește serviciul sau procesează din terminal.' : 'Eroare la procesare.';
                toast(errMsg, 'error');
                if ((window as any)._detailLineId) { const fresh = await api(`/api/line_detail/${(window as any)._detailLineId}`); (window as any)._detailData = fresh; renderDetailTab(); }
                return;
            }
            toast(isReprocess ? (data.message || 'Reprocesat.') : (data.message || `Procesat: ${data.intervals_added || 0} intervale, ${data.parts_added || 0} piese.`));
            if ((window as any)._detailLineId) {
                const fresh = await api(`/api/line_detail/${(window as any)._detailLineId}`);
                (window as any)._detailData = fresh;
                renderDetailTab();
            }
        } catch (err: unknown) {
            if (progressInterval != null) clearInterval(progressInterval);
            toast('Eroare: ' + (err instanceof Error ? err.message : String(err)), 'error');
        } finally {
            btn.disabled = false;
            if (statusEl) statusEl.textContent = '';
        }
    });
});

export function ensureReportsInNav(): void {
    const nav = document.querySelector('.nav-links');
    if (!nav) return;
    if (nav.querySelector('a[data-section="reports"]')) return;
    const journalLi = nav.querySelector('a[data-section="journal"]');
    const reportsLi = document.createElement('li');
    reportsLi.innerHTML = '<a href="#" data-section="reports"><span class="icon">📑</span> Rapoarte</a>';
    if (journalLi && journalLi.parentNode) {
        journalLi.parentNode.insertBefore(reportsLi, journalLi.nextSibling);
    } else {
        nav.appendChild(reportsLi);
    }
    if (!document.getElementById('sec-reports')) {
        const sec = document.createElement('section');
        sec.id = 'sec-reports';
        sec.className = 'section';
        sec.innerHTML = `
            <h2 class="section-heading">Rapoarte mentenanță și intervenții</h2>
            <p class="section-desc">Toate înregistrările sunt raportate la mașină și la categoria de intervenție. Filmările atașate sunt stocate în baza de date.</p>
            <div class="filter-bar">
                <label for="reports-machine">Mașină</label>
                <select id="reports-machine"><option value="">Toate</option></select>
                <label for="reports-category">Categorie</label>
                <select id="reports-category"><option value="">Toate</option></select>
                <label for="reports-from">De la data</label>
                <input type="date" id="reports-from">
                <label for="reports-to">Până la data</label>
                <input type="date" id="reports-to">
                <button type="button" id="reports-apply" class="btn">Aplică filtre</button>
                <button type="button" id="reports-reset" class="btn btn-outline">Resetează filtre</button>
            </div>
            <div class="reports-summary" id="reports-summary"></div>
            <div id="wf-reports-section" style="margin:16px 0;padding:16px;background:rgba(59,130,246,.06);border:1px solid var(--border);border-radius:10px">
                <h3 style="margin:0 0 10px;color:var(--accent)">Workflow-uri mentenanță — verificare și validare</h3>
                <div style="display:flex;gap:10px;align-items:center;margin-bottom:10px">
                    <label style="font-size:.85rem">Status: <select id="wf-reports-status"><option value="">Toate</option><option value="completed">Finalizate</option><option value="validated">Validate</option><option value="rejected">Respinse</option><option value="paused">În pauză</option><option value="in_progress">În desfășurare</option></select></label>
                    <button type="button" id="wf-reports-apply" class="btn btn-sm">Filtrează</button>
                </div>
                <div class="table-wrapper">
                    <table id="wf-reports-table">
                        <thead><tr><th>Data</th><th>Mașină</th><th>Acțiune</th><th>Operator</th><th>Ghidaj</th><th>Progres</th><th>Durată</th><th>Status</th><th>Verificare</th></tr></thead>
                        <tbody></tbody>
                    </table>
                </div>
            </div>
            <h3 class="section-heading" style="margin-top:32px">Jurnal mentenanță (istoric)</h3>
            <div class="table-wrapper">
                <table id="reports-table">
                    <thead><tr><th>Data</th><th>Mașină</th><th>Categorie</th><th>Acțiune</th><th>Operator</th><th>Ore</th><th>Durată</th><th>Filmare</th></tr></thead>
                    <tbody></tbody>
                </table>
            </div>
        `;
        const statsSec = document.getElementById('sec-statistics');
        if (statsSec && statsSec.parentNode) statsSec.parentNode.insertBefore(sec, statsSec);
        else document.querySelector('main#content')?.appendChild(sec);
    }
    reportsLi.querySelector('a')!.addEventListener('click', function(this: HTMLAnchorElement, e: Event) {
        e.preventDefault();
        $$('.nav-links a').forEach((x: Element) => x.classList.remove('active'));
        this.classList.add('active');
        $$('.section').forEach((s: Element) => s.classList.remove('active'));
        document.getElementById('sec-reports')?.classList.add('active');
        const pt = document.getElementById('page-title');
        if (pt) pt.textContent = 'Rapoarte';
        document.getElementById('sidebar')?.classList.remove('open');
        _closeProactivePanel();
        loadReports();
    });
}

$$('.detail-tab').forEach((tab: Element) => {
    tab.addEventListener('click', () => {
        setDetailTabActive((tab as HTMLElement).dataset.dtab || 'parts');
        $$('.detail-tab').forEach((t: Element) => t.classList.toggle('active', t === tab));
        renderDetailTab();
    });
});

$('#detail-close')?.addEventListener('click', () => {
    $('#detail-overlay')?.classList.add('hidden');
    document.querySelector('.detail-panel')?.classList.remove('maximized');
});
$('#detail-maximize')?.addEventListener('click', () => {
    document.querySelector('.detail-panel')?.classList.toggle('maximized');
});
$('#detail-overlay')?.addEventListener('click', (_e: Event) => {
    // ferestre se inchid doar manual (buton X)
});

document.body.addEventListener('click', function revisionDetailClick(e: MouseEvent) {
    const btn = (e.target as HTMLElement).closest('.btn-revision-detail');
    if (!btn || !btn.closest('#detail-content')) return;
    e.preventDefault();
    e.stopPropagation();
    const idx = parseInt(btn.getAttribute('data-revision-index') || '', 10);
    if (isNaN(idx) || idx < 0) return;
    const d = (window as any)._detailData;
    const machineId = (window as any)._detailLineId || (d && d.machine && d.machine.id);
    const machineName = (d && d.machine && d.machine.name) || machineId;
    const list = state.lastRevisionList;
    if (!list || !Array.isArray(list) || !list[idx]) return;
    openRevisionDetailView(list[idx], machineId, machineName);
});
